import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Button, Collapse, Form } from 'antd';
import { DownOutlined, ReloadOutlined } from '@ant-design/icons';
import QuillRichText from '../../../../components/QuillRichText';
import s from './s.module.less';

const { Panel } = Collapse;

interface CollapsibleConsentCardProps {
    title: string;
    description: string;
    content: string;
    defaultContent: string;
    fieldName: string;
    panelKey: string;
    activeKeys: string[];
    onActiveKeysChange: (keys: string[]) => void;
    onChange: (fieldName: string, value: string) => void;
}

const CollapsibleConsentCard = forwardRef<any, CollapsibleConsentCardProps>(({
    title,
    description,
    content,
    defaultContent,
    fieldName,
    panelKey,
    activeKeys,
    onActiveKeysChange,
    onChange,
}, ref) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editorModified, setEditorModified] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [error, setError] = useState<string>('');

    useEffect(() => {
        form.setFieldsValue({ [fieldName]: content });
    }, [content]);

    const toggleEditing = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newIsEditing = !isEditing;
        setIsEditing(newIsEditing);
    };

    const handleReset = () => {
        form.setFieldsValue({ [fieldName]: defaultContent });
        onChange(fieldName, defaultContent);
        setError('');
        setEditorModified(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        handleReset();
    };

    const handleSave = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();
            onChange(fieldName, values[fieldName]);
            setIsEditing(false);
        } catch (errorInfo) {
            setError(errorInfo.errorFields[0].errors[0]);
        }
    };

    useImperativeHandle(ref, () => ({
        onValidate: async () => {
            try {
                await form.validateFields();
            } catch (errorInfo) {
                setError(errorInfo.errorFields[0].errors[0]);
                throw errorInfo;
            }
        },
        isEditing,
    }));

    const handleValuesChange = () => {
        setError('');
        // ReactQuill 会默认去掉字符串之间的换行符
        if (form.getFieldValue(fieldName) !== defaultContent.replace(/\s+/g, ' ')) {
            setEditorModified(true);
        } else {
            setEditorModified(false);
        }
    };

    const getExpandIcon = (panelProps: { isActive?: boolean }) => {
        return <DownOutlined rotate={panelProps.isActive ? 180 : 0} style={{ color: '#000', fontSize: '15px' }} />;
    };

    return (
        <Collapse
            expandIcon={getExpandIcon}
            activeKey={activeKeys}
            onChange={(keys) => onActiveKeysChange(keys as string[])}
            className={s.formCollapse}
        >
            <Panel
                header={
                    <div className={s.panelHeader}>
                        <div className={s.panelTitle}>{title}</div>
                        <div className={s.panelDesc}>{description}</div>
                    </div>
                }
                key={panelKey}
                className={s.formPanel}
                extra={
                    activeKeys.includes(panelKey) && !isEditing && (
                        <Button
                            onClick={toggleEditing}
                        >
                            Edit
                        </Button>
                    )
                }
            >
                {isEditing ? (
                    <div className={s.editorContainer}>
                        <Form
                            form={form}
                            onValuesChange={handleValuesChange}
                        >
                            <Form.Item name={fieldName} noStyle rules={[{ required: true, message: 'This field can’t be empty. Please enter your agreement before saving.' }]}>
                                <QuillRichText
                                    maxLength={8000}
                                    height="320px"
                                    cls={s.editorContent}
                                />
                            </Form.Item>
                        </Form>
                        {error && <div className={s.errorMessage}>{error}</div>}
                        <div className={s.editorActions}>
                            <div className={s.actionButtons}>
                                <Button
                                    className={`${s.resetButton} ${!editorModified ? s.resetDisabled : ''}`}
                                    disabled={!editorModified}
                                    onClick={handleReset}
                                    type="text"
                                >
                                    <ReloadOutlined />
                                    Reset
                                </Button>
                                <Button
                                    className={s.cancelButton}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                >
                                    Save changes
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={s.contentPreview} dangerouslySetInnerHTML={{ __html: content }} />
                )}
            </Panel>
        </Collapse>
    );
});

export default CollapsibleConsentCard;
